import type { StateCreator } from 'zustand'

import type { ComposerProfile } from '~publish/legacy/composer/composer/stores/types'

import type { AppSlice } from '../appSlice'
import { profileToSelectableChannel } from './adapters'
import { canSelectChannel, shouldAutoSelectChannel } from './helpers'
import type { CapabilitySlice } from '../capabilitySlice'
import type { ComposerChannel } from '../hooks/useChannels'
import type { DirtySlice } from '../dirtySlice'
import type { EditorSlice } from '../editorSlice'
import type { ModeSlice } from '../modeSlice/modeSlice'
import type { PostSlice } from '../postSlice/postSlice'
import type { SaveErrorsSlice } from '../saveErrorsSlice'

export interface ChannelState {
  activeChannel: string
  selectedChannels: string[]
  channels: Record<string, ComposerChannel>
  /**
   * Legacy profiles
   * Store legacy profiles in new store to aid migration from legacy store
   * Will be replaced with channels
   */
  profiles: Record<string, ComposerProfile>
  /**
   * Whether the channel selector combobox is open
   * Persisted in state to survive component unmounts during omnibox transitions
   */
  isChannelSelectorOpen: boolean
  /**
   * Profile ids whose service draft has already been saved this composer
   * session (legacy `Draft.isSaved`, keyed by service name — all profiles
   * sharing a service lock together). Written solely by
   * `installFluxToProfileLockMirror`; not yet read by any production
   * consumer.
   */
  lockedProfileIds: string[]
}

export interface ChannelActions {
  /**
   * Select a single channel
   * Cannot be replaced by selectChannels as different rules apply
   */
  selectChannel: (channelId: string) => void
  /**
   * Deselect a single channel
   * Cannot be replaced by deselectChannels as different rules apply
   */
  deselectChannel: (channelId: string) => void
  /**
   * Select multiple channels
   * If no channelIds are provided, select all channels
   * Does not replace single selectChannel as different rules apply
   */
  selectChannels: (channelIds?: string[]) => void
  /**
   * Deselect multiple channels
   * If no channelIds are provided, deselect all channels
   * Does not replace single deselectChannel as different rules apply
   */
  deselectChannels: (channelIds?: string[]) => void
  /**
   * Replace the whole selection in one write: applies the same auto-select
   * rules as `selectChannels`, but from an empty base rather than the
   * current selection. Use this instead of `deselectChannels()` followed by
   * `selectChannels(channelIds)` for a "select exactly these" action (e.g.
   * a saved channel group) — two separate writes each notify the
   * Zustand→Flux selection mirror, and a channel present in both the old and
   * new selection can be dropped when the second write's diff is computed
   * before Flux has caught up with the first.
   */
  selectOnly: (channelIds: string[]) => void
  /**
   * Set the active channel being edited
   * This controls which editor is active
   */
  setActiveChannel: (channelId: string) => void
  /**
   * Set whether the channel selector combobox is open
   */
  setChannelSelectorOpen: (isOpen: boolean) => void
  /**
   * Patch mutable per-profile fields on an existing `profiles` entry — for
   * fields that continue to change via Flux actions after the initial seed
   * (e.g. Pinterest board selection). See
   * `installFluxToProfileFieldsMirror.ts`, the sole caller. No-ops for an id
   * not present in `profiles`.
   */
  updateProfileFields: (
    profileId: string,
    fields: Pick<ComposerProfile, 'selectedSubprofileId' | 'subprofiles'>,
  ) => void
  /** Written only by `installFluxToProfileLockMirror`. */
  setLockedProfileIds: (profileIds: string[]) => void
}

export type ChannelSlice = ChannelState & ChannelActions

// Shared by `selectChannels` and `selectOnly`: which of `candidates` should
// auto-select, given `alreadySelected` as the starting context for the
// per-channel exclusivity/group rules in `shouldAutoSelectChannel`.
function computeChannelsToSelect(
  candidates: ComposerProfile[],
  alreadySelected: ComposerProfile[],
): string[] {
  return candidates
    .reduce<ComposerProfile[]>((acc, channel) => {
      if (
        shouldAutoSelectChannel({
          channel: profileToSelectableChannel(channel),
          // Add channels that have already been selected, including channels that have been added to the accumulator
          channels: [
            ...alreadySelected.map(profileToSelectableChannel),
            ...acc.map(profileToSelectableChannel),
          ],
        })
      ) {
        acc.push(channel)
      }
      return acc
    }, [])
    .map((c) => c.id)
}

export const createChannelSlice: StateCreator<
  AppSlice &
    CapabilitySlice &
    ChannelSlice &
    DirtySlice &
    EditorSlice &
    ModeSlice &
    PostSlice &
    SaveErrorsSlice,
  [['zustand/devtools', never], ['zustand/immer', never]],
  [],
  ChannelSlice
> = (set) => ({
  activeChannel: 'omni',
  selectedChannels: [],
  channels: {},
  profiles: {},
  isChannelSelectorOpen: false,
  lockedProfileIds: [],
  selectChannel: (channelId): void =>
    set(
      (state) => {
        const channelToSelect = state.profiles[channelId]
        const selectedChannels = Object.values(state.profiles).filter((c) =>
          state.selectedChannels.includes(c.id),
        )

        // If the channel doesn't exist or can't be selected, do nothing
        if (
          !channelToSelect ||
          !canSelectChannel({
            channel: profileToSelectableChannel(channelToSelect),
            channels: selectedChannels.map(profileToSelectableChannel),
          })
        ) {
          return
        }

        state.selectedChannels.push(channelId)

        // If we're in omni mode and there's only one channel selected, set the active channel to the selected channel
        if (
          state.activeChannel === 'omni' &&
          state.selectedChannels.length === 1
        ) {
          state.activeChannel = channelId
        }
      },
      undefined,
      `channelSlice:addChannel/${channelId}`,
    ),
  deselectChannel: (channelId): void =>
    set(
      (state) => {
        const wasActive = state.activeChannel === channelId
        state.selectedChannels = state.selectedChannels.filter(
          (id) => id !== channelId,
        )
        // Deselecting the active channel leaves activeChannel dangling
        // otherwise: reset it based on what remains, same rule as selectOnly.
        if (wasActive) {
          state.activeChannel =
            state.selectedChannels.length === 1
              ? state.selectedChannels[0]
              : 'omni'
        }
      },
      undefined,
      `channelSlice:removeChannel/${channelId}`,
    ),
  selectChannels: (channelIds?: string[]): void =>
    set(
      (state) => {
        // If no channelIds are provided, select all channels
        const selectedChannels = Object.values(state.profiles).filter((c) =>
          state.selectedChannels.includes(c.id),
        )

        // A channel group can list an id no longer in `state.profiles` (e.g.
        // disconnected since the group was saved) — skip it rather than
        // letting the whole batch throw on a missing profile.
        const channels = channelIds
          ? channelIds
              .map((id) => state.profiles[id])
              .filter((profile): profile is ComposerProfile => !!profile)
          : Object.values(state.profiles)

        const channelsToSelect = computeChannelsToSelect(
          channels,
          selectedChannels,
        )

        state.selectedChannels = [
          ...state.selectedChannels,
          ...channelsToSelect,
        ]

        // If we're in omni mode and there's only one channel selected, set the active channel to the selected channel
        if (
          state.activeChannel === 'omni' &&
          state.selectedChannels.length === 1
        ) {
          state.activeChannel = state.selectedChannels[0]
        }
      },
      undefined,
      `channelSlice:selectChannels`,
    ),
  selectOnly: (channelIds: string[]): void =>
    set(
      (state) => {
        const channels = channelIds
          .map((id) => state.profiles[id])
          .filter((profile): profile is ComposerProfile => !!profile)

        state.selectedChannels = computeChannelsToSelect(channels, [])

        state.activeChannel =
          state.selectedChannels.length === 1
            ? state.selectedChannels[0]
            : 'omni'
      },
      undefined,
      `channelSlice:selectOnly`,
    ),
  deselectChannels: (channelIds?: string[]): void =>
    set(
      (state) => {
        // Track whether the active channel is one of the ones being removed,
        // before mutating the selection.
        const wasActive = channelIds
          ? channelIds.includes(state.activeChannel)
          : state.selectedChannels.includes(state.activeChannel)

        // If channelIds are provided, deselect only the specified channels
        if (channelIds) {
          state.selectedChannels = state.selectedChannels.filter(
            (id) => !channelIds.includes(id),
          )
        } else {
          // If no channelIds are provided, deselect all channels
          state.selectedChannels = []
        }

        // Deselecting the active channel leaves activeChannel dangling
        // otherwise: reset it based on what remains, same rule as selectOnly.
        if (wasActive) {
          state.activeChannel =
            state.selectedChannels.length === 1
              ? state.selectedChannels[0]
              : 'omni'
        }
      },
      undefined,
      `channelSlice:deselectChannels`,
    ),
  setActiveChannel: (channelId): void =>
    set(
      (state) => {
        // If the channel doesn't exist or isn't selected, do nothing
        if (
          !state.profiles[channelId] ||
          !state.selectedChannels.includes(channelId)
        ) {
          return
        }
        state.activeChannel = channelId
      },
      undefined,
      `channelSlice:setActiveChannel/${channelId}`,
    ),
  setChannelSelectorOpen: (isOpen): void =>
    set(
      (state) => {
        state.isChannelSelectorOpen = isOpen
      },
      undefined,
      `channelSlice:setChannelSelectorOpen`,
    ),
  updateProfileFields: (profileId, fields): void =>
    set(
      (state) => {
        const profile = state.profiles[profileId]
        if (!profile) return
        profile.selectedSubprofileId = fields.selectedSubprofileId
        profile.subprofiles = fields.subprofiles
      },
      undefined,
      `channelSlice:updateProfileFields/${profileId}`,
    ),
  setLockedProfileIds: (profileIds): void =>
    set(
      (state) => {
        state.lockedProfileIds = profileIds
      },
      undefined,
      'channelSlice:setLockedProfileIds',
    ),
})
